// screens/ClaimScreen.js
// BuzzInApp — Pantalla de canje presencial
//
// Mejoras v2:
//   • Banner naranja de "faltan X clicks" arriba del scanner cuando !canClaim
//   • Distancia formateada como pill (ej: "142m") en el geo indicator
//   • Botón deshabilitado más descriptivo ("Compartí primero")
//   • Esquinas decorativas en el marco de scan

import React, { useState, useEffect, useRef } from "react";
import { startScanner, stopScanner, isCameraAvailable } from "../security/qrScanner";
import useGeolocation from "../hooks/useGeolocation";
import useClicks from "../hooks/useClicks";
import useRedemption, { REDEMPTION_STATUS, REDEMPTION_ERRORS } from "../hooks/useRedemption";
import { validatePresence } from "../security/geoFence";
import useTranslation from "../i18n/useTranslation";
import "./ClaimScreen.css";

const IconBack = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconShare = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <circle cx="6"  cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const ClaimScreen = ({ business, authHook, onSuccess, onBack }) => {
  const { t } = useTranslation();
  const { user, profile } = authHook;

  const geo = useGeolocation({ watchMode: true, business });

  const geoResult = geo.position
    ? validatePresence(geo.position, business)
    : null;

  const { clickCount, linkId, canClaim } = useClicks(user?.uid, business);
  const { status, error, errorData, redeem, isVerifying } = useRedemption({
    user, profile, geoResult, clickCount, linkId,
  });

  const [scanning, setScanning]       = useState(false);
  const [cameraAvail, setCameraAvail] = useState(true);
  const [scanError, setScanError]     = useState(null);
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => { isCameraAvailable().then(setCameraAvail); }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) stopScanner(streamRef.current);
    };
  }, []);

  const handleStartScan = async () => {
    setScanError(null);
    setScanning(true);
    const stream = await startScanner(
      videoRef.current, canvasRef.current,
      handleQRResult, handleScanError
    );
    streamRef.current = stream;
  };

  const handleQRResult = async (qrString) => {
    setScanning(false);
    if (streamRef.current) {
      stopScanner(streamRef.current);
      streamRef.current = null;
    }
    const result = await redeem(qrString);
    if (result.success) onSuccess(result);
  };

  const handleScanError = (err) => {
    setScanning(false);
    setScanError(
      err.message === "camera_permission_denied"
        ? t("claim.errors.cameraPermission")
        : t("claim.errors.networkError")
    );
  };

  const getErrorMessage = () => {
    if (!error) return null;
    switch (error) {
      case REDEMPTION_ERRORS.QR_EXPIRED:        return t("claim.qrExpired");
      case REDEMPTION_ERRORS.QR_INVALID:        return t("claim.qrInvalid");
      case REDEMPTION_ERRORS.TOO_FAR:           return t("claim.tooFarAway", { distance: errorData.distance ?? "?" });
      case REDEMPTION_ERRORS.MOCK_LOCATION:     return t("claim.mockLocation");
      case REDEMPTION_ERRORS.NOT_ENOUGH_CLICKS: return t("claim.notEnoughClicks", { needed: errorData.needed, current: errorData.current });
      case REDEMPTION_ERRORS.ALREADY_CLAIMED:   return t("claim.alreadyClaimed", { days: errorData.days ?? 30 });
      case REDEMPTION_ERRORS.RATE_LIMITED:      return t("claim.rateLimited");
      case REDEMPTION_ERRORS.NETWORK_ERROR:     return t("claim.errors.networkError");
      default:                                   return t("claim.errors.serverError");
    }
  };

  // Distancia formateada
  const distanceRaw = geoResult?.distanceMeters;
  const distanceLabel = distanceRaw != null
    ? distanceRaw < 1000 ? `${Math.round(distanceRaw)}m` : `${(distanceRaw / 1000).toFixed(1)}km`
    : null;

  const isPresent = geoResult?.present ?? false;

  // Clicks restantes para canClaim
  const needed    = business?.prize_clicks_needed ?? 1;
  const remaining = Math.max(0, needed - clickCount);

  return (
    <div className="claim-screen">
      <header className="claim-screen__header">
        <button
          className="claim-screen__back"
          onClick={onBack}
          aria-label={t("app.back")}
          disabled={isVerifying}
        >
          <IconBack />
        </button>
        <div className="claim-screen__header-title">
          <span>{business?.icon || "🏪"}</span>
          <span>{business?.name}</span>
        </div>
        <div style={{ width: 40 }} />
      </header>

      <div className="claim-screen__body">
        {/* Indicador de ubicación */}
        <div className={`claim-screen__geo ${isPresent ? "claim-screen__geo--ok" : "claim-screen__geo--warn"}`}>
          <div className="claim-screen__geo-dot" aria-hidden="true" />
          <span className="claim-screen__geo-text">
            {geo.loading
              ? t("claim.locationRequired")
              : !geo.available
              ? t("claim.locationDenied")
              : isPresent
              ? t("claim.locationGranted")
              : t("claim.tooFarAway", { distance: distanceLabel ?? "?" })
            }
          </span>
          {distanceLabel && (
            <span className="claim-screen__geo-distance">{distanceLabel}</span>
          )}
        </div>

        {/* Mock location warning */}
        {geo.mockDetected && (
          <div className="claim-screen__mock-warning" role="alert">
            ⚠️ {t("claim.mockLocation")}
          </div>
        )}

        {/* Banner de clicks insuficientes — visible y accionable */}
        {!canClaim && (
          <div className="claim-screen__clicks-needed" role="status">
            <IconShare />
            Necesitás {remaining} {remaining === 1 ? "click más" : "clicks más"} para canjear
          </div>
        )}

        {/* Área del scanner */}
        <div className="claim-screen__scanner-wrap">
          <video
            ref={videoRef}
            className={`claim-screen__video ${scanning ? "claim-screen__video--active" : ""}`}
            playsInline muted
            aria-label="Vista de cámara para escanear QR"
          />
          <canvas ref={canvasRef} className="claim-screen__canvas" aria-hidden="true" />

          {!scanning && status !== REDEMPTION_STATUS.VERIFYING && (
            <div className="claim-screen__scanner-overlay">
              <div className="claim-screen__scanner-icon" aria-hidden="true">
                <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <rect x="2"  y="2"  width="18" height="18" rx="2" stroke="var(--accent)" strokeWidth="2.5"/>
                  <rect x="44" y="2"  width="18" height="18" rx="2" stroke="var(--accent)" strokeWidth="2.5"/>
                  <rect x="2"  y="44" width="18" height="18" rx="2" stroke="var(--accent)" strokeWidth="2.5"/>
                  <rect x="6"  y="6"  width="10" height="10" fill="var(--accent)" rx="1"/>
                  <rect x="48" y="6"  width="10" height="10" fill="var(--accent)" rx="1"/>
                  <rect x="6"  y="48" width="10" height="10" fill="var(--accent)" rx="1"/>
                  <line x1="32" y1="26" x2="32" y2="38" stroke="var(--text-muted)" strokeWidth="1.5" strokeDasharray="3 3"/>
                  <line x1="26" y1="32" x2="38" y2="32" stroke="var(--text-muted)" strokeWidth="1.5" strokeDasharray="3 3"/>
                </svg>
              </div>
              <p className="claim-screen__scan-hint">
                {canClaim ? t("claim.scanInstructions") : "Compartí primero para acumular clicks"}
              </p>
            </div>
          )}

          {isVerifying && (
            <div className="claim-screen__verifying" aria-live="polite">
              <span className="claim-screen__spinner" />
              <span>{t("claim.verifying")}</span>
            </div>
          )}

          {scanning && (
            <div className="claim-screen__scan-frame" aria-hidden="true">
              <div className="claim-screen__scan-line" />
            </div>
          )}
        </div>

        {/* Error */}
        {(scanError || (error && status === REDEMPTION_STATUS.FAILED)) && (
          <div className="claim-screen__error" role="alert">
            {scanError || getErrorMessage()}
          </div>
        )}

        {/* Botón principal */}
        {!cameraAvail ? (
          <div className="claim-screen__no-camera">
            {t("claim.errors.cameraPermission")}
          </div>
        ) : (
          <button
            className={`claim-screen__scan-btn ${scanning ? "claim-screen__scan-btn--active" : ""}`}
            onClick={scanning ? undefined : handleStartScan}
            disabled={isVerifying || !canClaim}
            aria-busy={scanning || isVerifying}
            aria-label={
              !canClaim
                ? `Necesitás ${remaining} clicks más para canjear`
                : t("claim.scanButton")
            }
          >
            {scanning
              ? t("claim.scanInstructions")
              : isVerifying
              ? t("claim.verifying")
              : !canClaim
              ? `Faltan ${remaining} clicks`
              : t("claim.scanButton")
            }
          </button>
        )}
      </div>
    </div>
  );
};

export default ClaimScreen;